import React from 'react';
import {fetchWarningsSummary} from '../api/actions';
import type {WarningsSummaryPoint} from '../api/actions';
import type {PointLocation, WarningsSummaryItem} from '../api/types';
import useApiClient, {ApiError} from './useApiClient';

/** A point, or null for a row that has none (it gets no chip). */
type MaybePoint = WarningsSummaryPoint | null | undefined;

export type UseWarningsSummaryOptions = {
  /**
   * False: no request, every item null. The station lists that show no chip
   * (nearby stations, search results) pass false so they cost nothing.
   */
  enabled?: boolean;
};

type UseWarningsSummaryResult = {
  /**
   * One per point, in the order given: the point's badge, or null (no
   * warning there, no point, not loaded yet, or the request failed).
   */
  items: (WarningsSummaryItem | null)[];
  /** A request is in flight (a refresh keeps the previous items meanwhile). */
  loading: boolean;
  /**
   * The last request failed for a reason other than HTTP (an `ApiError`,
   * which the backend logs): a broken contract, e.g. an answer whose items
   * do not line up with the points. Core has no error reporter, so the app
   * reports it to its own Sentry; the object is the same across renders, so
   * an effect keyed on it reports each failure once. Either way the list
   * shows its rows without chips, never an error state.
   */
  unexpectedError: unknown | null;
  /**
   * Reads the summary again (list focus, app foreground), unless the last
   * answer for these points is under `SUMMARY_FRESH_MS` old: returning to the
   * list a few seconds later costs nothing. Never polled.
   */
  refresh: () => void;
};

/** How long an answer is fresh enough that `refresh()` skips the request. */
export const SUMMARY_FRESH_MS = 60_000;

/** A station's GeoJSON point (longitude first) as a summary point. */
export const stationWarningPoint = (station: {
  location?: PointLocation | null;
}): WarningsSummaryPoint | null => {
  const [lon, lat] = station.location?.coordinates ?? [];
  return typeof lat === 'number' &&
    typeof lon === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lon)
    ? {lat, lon}
    : null;
};

const pointKey = (point: MaybePoint): string | null =>
  point &&
  typeof point.lat === 'number' &&
  typeof point.lon === 'number' &&
  Number.isFinite(point.lat) &&
  Number.isFinite(point.lon)
    ? `${point.lat},${point.lon}`
    : null;

type Settled = {
  /** The request it answers: the points and the refresh count. */
  token: string;
} & (
  | {byPoint: Map<string, WarningsSummaryItem | null>; failure: null}
  | {byPoint: null; failure: {error: unknown | null}}
);

/**
 * The warning badge of every station in a list, from `warnings/summary/`
 * (`fetchWarningsSummary`: at most 100 points a request, re-joined in order).
 *
 * Read once per list open and whenever the set of points changes, plus on
 * a `refresh()` once the last answer is over a minute old; never polled.
 * Identical points are sent once. Silent (no error snackbar): a failure clears
 * the chips, and one that is not HTTP is handed to the app to report
 * (`unexpectedError`).
 *
 * Keyed by the SET of points (sorted, so reordering the rows does not
 * refetch), like `useWarningAreas`: only the latest
 * request's answer lands, so a slow answer for the list as it was can never
 * overwrite a newer one. Until the answer for a new set (or a refresh)
 * arrives, a point the previous answer covered keeps its chip: adding a
 * station does not blank the others.
 */
const useWarningsSummary = (
  points: readonly MaybePoint[],
  {enabled = true}: UseWarningsSummaryOptions = {},
): UseWarningsSummaryResult => {
  const {fetch} = useApiClient();

  // Strings, so a list re-rendered with equal points neither refetches nor
  // hands its rows new items.
  const keys = JSON.stringify(points.map(pointKey));
  const unique = [
    ...new Set(
      (JSON.parse(keys) as (string | null)[]).filter(
        (k): k is string => k !== null,
      ),
    ),
  ];
  const requestKey =
    enabled && unique.length > 0 ? unique.sort().join('|') : null;

  // When the current points last got a successful answer: `refresh()` skips
  // the request while it is fresh. A failed answer is never fresh.
  const answered = React.useRef<{key: string; at: number} | null>(null);
  const [refreshes, setRefreshes] = React.useState(0);
  const refresh = React.useCallback(() => {
    const last = answered.current;
    if (
      last &&
      last.key === requestKey &&
      Date.now() - last.at < SUMMARY_FRESH_MS
    ) {
      return;
    }
    setRefreshes((n) => n + 1);
  }, [requestKey]);
  const token = requestKey === null ? null : `${refreshes}#${requestKey}`;
  const [settled, setSettled] = React.useState<Settled | null>(null);

  React.useEffect(() => {
    if (token === null || requestKey === null) {
      return undefined;
    }
    // Cleared when the points change, on refresh and on unmount: a later
    // answer is not for what is on screen.
    let live = true;
    const sent = requestKey.split('|');
    // `${lat},${lon}` of finite numbers: `Number` reads them back exactly.
    const request = sent.map((key) => {
      const [lat, lon] = key.split(',').map(Number);
      return {lat, lon};
    });
    fetchWarningsSummary(fetch, request, {silent: true}).then(
      (response) => {
        if (live) {
          answered.current = {key: requestKey, at: Date.now()};
          setSettled({
            token,
            byPoint: new Map(
              sent.map((key, i) => [key, response.items[i] ?? null]),
            ),
            failure: null,
          });
        }
      },
      (error: unknown) => {
        if (live) {
          answered.current = null;
          setSettled({
            token,
            byPoint: null,
            failure: {error: error instanceof ApiError ? null : error},
          });
        }
      },
    );
    return () => {
      live = false;
    };
  }, [token, requestKey, fetch]);

  const byPoint = enabled ? (settled?.byPoint ?? null) : null;
  const items = React.useMemo(
    () =>
      (JSON.parse(keys) as (string | null)[]).map((key) =>
        key !== null && byPoint ? (byPoint.get(key) ?? null) : null,
      ),
    [keys, byPoint],
  );

  return React.useMemo(
    () => ({
      items,
      loading: token !== null && settled?.token !== token,
      unexpectedError: (enabled && settled?.failure?.error) || null,
      refresh,
    }),
    [items, token, settled, enabled, refresh],
  );
};

export default useWarningsSummary;
